/**
 * Ported VERBATIM from apr_template `src/pages/ImplementationRoadmap.tsx` (Planning & Integration).
 * Only import paths change, plus anything noted inline as a PORT NOTE.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { authFetch } from '../compat/api';
import PageHeader from '../compat/PageHeader';
import HelpPanel from '../compat/HelpPanel';
import type { Help } from '../compat/helpTypes';
import './ImplementationRoadmap.css';
// Decision Workspace design (owner ruling 2026-10-21): kit icons, notices and tokens.
import { Icon, icons } from '@platform/ui-kit/workspace';
import { WS_COLOR, WS_FONT, WS_STATUS, WS_TYPE, wsType } from '../workspace/ws';
import { useWsDialogs } from '../workspace/useWsDialogs';
import { dayKey, holidaysBetween } from '../compat/ganttCalendar';
import { DispositionChip } from '../compat/DispositionChip';
import { GroupMultiSelect, groupFetchKey } from '../workspace/GroupMultiSelect';

// Owner request 2026-11-03: the application's disposition beside its ID, on the Gantt and in
// the table. The value is `application_status` (= applications_master.recommendation, from
// /roadmap-schedule/visualization/gantt). Shared with Roadmap Schedule and Application
// Interdependencies: compat/DispositionChip.tsx.

const ImplementationRoadmapHelp: Help = {
  title: 'Implementation Roadmap',
  icon: '🗺️',
  purpose: 'A high-level, read-only visualization of the sequenced implementation roadmap &mdash; each application&rsquo;s phase waves laid out on a timeline so you can see the overall wave/sequencing at a glance.',
  doHere: [
    'Select a <b>Grouping</b> (or All Groupings) to load its scheduled applications.',
    'Switch between the <b>Gantt Chart</b> and <b>Table</b> views.',
    'In Gantt view, set a <b>Start Date</b> and use <b>Zoom</b> to adjust the timeline density.',
    'Click <b>Download Data</b> to export the roadmap and phase details to Excel.',
    'Hover a phase bar to see its start/end dates, base duration, and buffer.',
  ],
  fits: 'Integration &rarr; Implementation Planning &rarr; High-Level Implementation Roadmap',
  firstTime: 'Choose an integration group; if nothing appears, calculate its schedule first on the Roadmap Schedule screen.',
  sections: [
    {
      type: 'defs',
      label: 'Implementation phases',
      items: [
        { term: 'Assessment & Planning', color: '#3B82F6', def: 'Scoping and preparation before design.' },
        { term: 'Design & Architecture', color: '#A855F7', def: 'Target design and architecture work.' },
        { term: 'Migration Execution', color: '#22C55E', def: 'The core migration/build effort.' },
        { term: 'Testing & Validation', color: '#F59E0B', def: 'Verification before go-live.' },
        { term: 'Go-Live & Optimization', color: '#EF4444', def: 'Cutover and post-launch tuning.' },
        { term: 'Deployed / Completed', color: '#9E9E9E', def: 'Finished apps render in grayscale.' },
      ],
      note: 'A red dashed line marks today; weekends and US federal holidays are shaded as non-business days.',
    },
  ],
};

// ============================================================================
// Type Definitions
// ============================================================================

interface PhaseData {
  name: string;
  base_duration: number;
  buffer_days: number;
  total_duration: number;
  start_date: string | null;
  end_date: string | null;
}

interface ApplicationData {
  id: string;
  text: string;
  start_date: string | null;
  end_date: string | null;
  integration_group_id: string;
  integration_group_name: string;
  application_status: string;
  schedule_status: string;
  total_duration_days: number;
  total_effort_days: number;
  phases: PhaseData[];
  deployment_status?: string;
}

interface DeploymentStatusRecord {
  integration_group_id: string;
  application_id: string;
  deployment_status: string;
}

interface IntegrationGroup {
  group_id: string;
  group_name: string;
  app_count?: number;
}

// ============================================================================
// Constants
// ============================================================================

// DW: phase colours ENCODE the five implementation phases (Gantt bars, legend, table phase
// columns) and the grey encodes Deployed/Completed, so they stay as data colours, not kit chrome.
const PHASE_COLORS: Record<string, { primary: string; secondary: string; border: string; bg: string }> = {
  'Assessment & Planning': {
    primary: '#60A5FA',    // Medium blue
    secondary: '#3B82F6',
    border: '#2563EB',
    bg: '#EFF6FF'
  },
  'Design & Architecture': {
    primary: '#C084FC',    // Medium purple
    secondary: '#A855F7',
    border: '#9333EA',
    bg: '#FAF5FF'
  },
  'Migration Execution': {
    primary: '#4ADE80',    // Medium green
    secondary: '#22C55E',
    border: '#16A34A',
    bg: '#F0FDF4'
  },
  'Testing & Validation': {
    primary: '#FBBF24',    // Medium yellow/amber
    secondary: '#F59E0B',
    border: '#D97706',
    bg: '#FFFBEB'
  },
  'Go-Live & Optimization': {
    primary: '#F87171',    // Medium red/coral
    secondary: '#EF4444',
    border: '#DC2626',
    bg: '#FEF2F2'
  }
};

// DEVIATION (owner request 2026-11-03: Roadmap Visualization must look like the redesigned
// Roadmap Schedule Gantt): the Gantt bars and legend use the SAME soft fill-only phase colours
// as RoadmapSchedule.tsx (PHASE_COLORS there), no gradient, no outline. PHASE_COLORS above is
// left for the table view's phase headers, which carry white text.
const PHASE_SOFT_FILL: Record<string, string> = {
  'Assessment & Planning': '#BFDBFE',
  'Design & Architecture': '#E9D5FF',
  'Migration Execution': '#BBF7D0',
  'Testing & Validation': '#FDE68A',
  'Go-Live & Optimization': '#FECACA'
};
const SOFT_GREY_FILL = '#E0E0E0'; // Deployed / Completed, soft like the phases
// The Roadmap Schedule Gantt's calendar values (RoadmapSchedule.tsx GANTT_* constants).
const GANTT_WEEKEND_FILL = '#efe9e1';
const GANTT_HOLIDAY_BASE = '#f5e9da';
const GANTT_HOLIDAY_HATCH = '#e3cfb6';
const GANTT_HOLIDAY_INK = '#8c3a00';
const GANTT_DAY_RULE = '#c8ccd1';

const GRAYSCALE_COLORS = {
  primary: '#9E9E9E',
  secondary: '#757575',
  border: '#616161'
};

// DW: the canvas chrome (grid, row fills, header text, today marker) reads kit tokens; the canvas
// cannot take CSS, so the font is the kit sans at a kit type size.
const canvasFont = (size: number, weight: number | string = 400): string => `${weight} ${size}px ${WS_FONT.sans}`;

const PHASE_ORDER = [
  'Assessment & Planning',
  'Design & Architecture',
  'Migration Execution',
  'Testing & Validation',
  'Go-Live & Optimization'
];

const US_FEDERAL_HOLIDAYS = [
  { month: 1, day: 1, name: "New Year's Day" },
  { month: 1, day: 15, weekday: 1, week: 3, name: "Martin Luther King Jr. Day" },
  { month: 2, day: 15, weekday: 1, week: 3, name: "Presidents' Day" },
  { month: 5, day: 25, weekday: 1, week: -1, name: "Memorial Day" },
  { month: 6, day: 19, name: "Juneteenth" },
  { month: 7, day: 4, name: "Independence Day" },
  { month: 9, day: 1, weekday: 1, week: 1, name: "Labor Day" },
  { month: 10, day: 8, weekday: 1, week: 2, name: "Columbus Day" },
  { month: 11, day: 11, name: "Veterans Day" },
  { month: 11, day: 22, weekday: 4, week: 4, name: "Thanksgiving Day" },
  { month: 12, day: 25, name: "Christmas Day" }
];

// ============================================================================
// Utility Functions
// ============================================================================

const isWeekend = (date: Date): boolean => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

const getHolidayDate = (holiday: any, year: number): Date | null => {
  if (holiday.weekday !== undefined && holiday.week !== undefined) {
    // Calculate nth weekday of month
    const firstOfMonth = new Date(year, holiday.month - 1, 1);
    let count = 0;
    const targetWeekday = holiday.weekday;

    if (holiday.week === -1) {
      // Last occurrence
      const lastOfMonth = new Date(year, holiday.month, 0);
      for (let d = lastOfMonth.getDate(); d >= 1; d--) {
        const testDate = new Date(year, holiday.month - 1, d);
        if (testDate.getDay() === targetWeekday) {
          return testDate;
        }
      }
    } else {
      // Nth occurrence
      for (let d = 1; d <= 31; d++) {
        const testDate = new Date(year, holiday.month - 1, d);
        if (testDate.getMonth() !== holiday.month - 1) break;
        if (testDate.getDay() === targetWeekday) {
          count++;
          if (count === holiday.week) {
            return testDate;
          }
        }
      }
    }
    return null;
  }
  return new Date(year, holiday.month - 1, holiday.day);
};

const isHoliday = (date: Date): boolean => {
  const year = date.getFullYear();
  for (const holiday of US_FEDERAL_HOLIDAYS) {
    const holidayDate = getHolidayDate(holiday, year);
    if (holidayDate &&
        holidayDate.getFullYear() === date.getFullYear() &&
        holidayDate.getMonth() === date.getMonth() &&
        holidayDate.getDate() === date.getDate()) {
      return true;
    }
  }
  return false;
};

const isBusinessDay = (date: Date): boolean => {
  return !isWeekend(date) && !isHoliday(date);
};

const parseDate = (dateStr: string | null): Date | null => {
  if (!dateStr) return null;
  // Handle ISO format dates with timezone (e.g., "2026-01-05T05:00:00.000Z")
  // and simple date strings (e.g., "2026-01-05")
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) {
    // Try adding time component for simple date strings
    const dateWithTime = new Date(dateStr + 'T00:00:00');
    return isNaN(dateWithTime.getTime()) ? null : dateWithTime;
  }
  return date;
};

const formatDate = (date: Date | null): string => {
  if (!date) return '';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const formatDateShort = (date: Date | null): string => {
  if (!date) return '';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
};

const formatDateMMDDYYYY = (date: Date | null): string => {
  if (!date) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

// DEVIATION (2026-11-03, day-level Gantt): counted on local calendar days and rounded. The
// source's Math.ceil over raw milliseconds counts one day too many across the autumn clock
// change (25-hour day), which shifts every column after it — invisible at month scale.
const daysBetween = (start: Date, end: Date): number => {
  const a = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const b = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.round((b.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));
};

// Weekend / holiday kind of a day, using the SCHEDULER's holiday list (compat/ganttCalendar),
// so the shading matches the days the schedule calculation skips.
const nonWorkingKind = (date: Date, holidays: Map<string, string>): 'holiday' | 'weekend' | null =>
  holidays.has(dayKey(date)) ? 'holiday' : isWeekend(date) ? 'weekend' : null;

// Fills one day column with the weekend fill, or the holiday base + diagonal hatch.
const shadeDay = (ctx: CanvasRenderingContext2D, kind: 'holiday' | 'weekend', x: number, y: number, w: number, h: number) => {
  ctx.fillStyle = kind === 'holiday' ? GANTT_HOLIDAY_BASE : GANTT_WEEKEND_FILL;
  ctx.fillRect(x, y, w, h);
  if (kind !== 'holiday') return;
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.clip();
  ctx.strokeStyle = GANTT_HOLIDAY_HATCH;
  ctx.lineWidth = 2;
  for (let k = -h; k < w + h; k += 7) {
    ctx.beginPath();
    ctx.moveTo(x + k, y + h);
    ctx.lineTo(x + k + h, y);
    ctx.stroke();
  }
  ctx.restore();
};

// Calculate business days between two dates (excluding weekends and holidays)
const businessDaysBetween = (start: Date, end: Date): number => {
  let count = 0;
  const current = new Date(start);

  while (current <= end) {
    if (isBusinessDay(current)) {
      count++;
    }
    current.setDate(current.getDate() + 1);
  }

  return count;
};

// ============================================================================
// Main Component
// ============================================================================

const ImplementationRoadmap: React.FC = () => {
  // State
  const [applications, setApplications] = useState<ApplicationData[]>([]);
  const [deploymentStatuses, setDeploymentStatuses] = useState<DeploymentStatusRecord[]>([]);
  const [integrationGroups, setIntegrationGroups] = useState<IntegrationGroup[]>([]);
  // Several groups may be picked (Ctrl-click): they load ALL and are narrowed on screen.
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const selectedGroupId = groupFetchKey(selectedGroupIds);
  const pickedKey = selectedGroupIds.join('|');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState<'gantt' | 'table'>('gantt');
  const [zoomLevel, setZoomLevel] = useState(1);
  const [hoveredApp, setHoveredApp] = useState<string | null>(null);
  const [hoveredPhase, setHoveredPhase] = useState<{ appId: string; phase: string } | null>(null);
  const [ganttStartDate, setGanttStartDate] = useState<string>('');
  const { notify, dialogs } = useWsDialogs();

  // Refs
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);

  // Constants for Gantt layout
  const SIDEBAR_WIDTH = 280;
  const ROW_HEIGHT = 50;
  const HEADER_HEIGHT = 60;
  const DAY_WIDTH = 20 * zoomLevel;

  // Calculate timeline bounds
  const getTimelineBounds = useCallback(() => {
    const allDates: Date[] = [];
    applications.forEach(app => {
      const startDate = parseDate(app.start_date);
      const endDate = parseDate(app.end_date);
      if (startDate) allDates.push(startDate);
      if (endDate) allDates.push(endDate);
      app.phases.forEach(phase => {
        const phaseStart = parseDate(phase.start_date);
        const phaseEnd = parseDate(phase.end_date);
        if (phaseStart) allDates.push(phaseStart);
        if (phaseEnd) allDates.push(phaseEnd);
      });
    });

    if (allDates.length === 0) {
      const today = new Date();
      return {
        minDate: today,
        maxDate: new Date(today.getTime() + 90 * 24 * 60 * 60 * 1000),
        totalDays: 90
      };
    }

    const validDates = allDates.filter(d => d !== null && !isNaN(d.getTime()));
    if (validDates.length === 0) {
      const today = new Date();
      return {
        minDate: today,
        maxDate: new Date(today.getTime() + 90 * 24 * 60 * 60 * 1000),
        totalDays: 90
      };
    }

    // Use user-selected start date if provided, otherwise auto-detect from data
    let minDate: Date;
    if (ganttStartDate) {
      minDate = new Date(ganttStartDate + 'T00:00:00');
    } else {
      minDate = new Date(Math.min(...validDates.map(d => d.getTime())));
      minDate.setDate(minDate.getDate() - 7);
    }

    const maxDate = new Date(Math.max(...validDates.map(d => d.getTime())));
    maxDate.setDate(maxDate.getDate() + 14);

    // Ensure maxDate is after minDate
    if (maxDate <= minDate) {
      maxDate.setTime(minDate.getTime() + 90 * 24 * 60 * 60 * 1000);
    }

    return {
      minDate,
      maxDate,
      totalDays: daysBetween(minDate, maxDate)
    };
  }, [applications, ganttStartDate]);

  // Fetch integration groups
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await authFetch('/api/integration-groups?exclude_analysis=true');
        const result = await response.json();
        if (result.success) {
          setIntegrationGroups(result.data || []);
        }
      } catch (err) {
        console.error('Error fetching integration groups:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchGroups();
  }, []);

  // Fetch roadmap data when group changes
  useEffect(() => {
    if (!selectedGroupId) {
      setApplications([]);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const params = new URLSearchParams();
        // If "ALL" is selected, don't filter by group - fetch all
        if (selectedGroupId !== 'ALL') {
          params.append('integration_group_id', selectedGroupId);
        }

        // Fetch roadmap data and deployment statuses in parallel
        const [roadmapResponse, deploymentResponse] = await Promise.all([
          authFetch(`/api/roadmap-schedule/visualization/gantt?${params.toString()}`),
          authFetch('/api/deployment-status')
        ]);

        const result = await roadmapResponse.json();
        let deploymentData: DeploymentStatusRecord[] = [];
        try {
          const depResult = await deploymentResponse.json();
          if (Array.isArray(depResult)) {
            deploymentData = depResult;
            setDeploymentStatuses(depResult);
          }
        } catch (e) {
          // Ignore deployment status errors
        }

        if (result.success) {
          // Merge deployment statuses into applications
          const picked = (result.data || []).filter((app: ApplicationData) => selectedGroupIds.length < 2 || selectedGroupIds.includes(app.integration_group_id));
          const appsWithStatus = picked.map((app: ApplicationData) => {
            const depStatus = deploymentData.find(
              ds => ds.integration_group_id === app.integration_group_id && ds.application_id === app.id
            );
            return {
              ...app,
              deployment_status: depStatus?.deployment_status || undefined
            };
          });
          setApplications(appsWithStatus);
        } else {
          setError(result.error || 'Failed to fetch data');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch roadmap data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [pickedKey]);

  // Draw Gantt chart on canvas (rows only, header is separate)
  const drawGanttChart = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || applications.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { minDate, totalDays } = getTimelineBounds();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const canvasWidth = totalDays * DAY_WIDTH;
    // Canvas height is ONLY for rows (no header) - this ensures sync with sidebar
    const canvasHeight = applications.length * ROW_HEIGHT;

    // Set canvas size
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    // Clear canvas
    ctx.fillStyle = WS_COLOR.surface;
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // DEVIATION (owner request 2026-11-03, the Roadmap Schedule Gantt's calendar): the source
    // drew the day grid and weekend shading FIRST and then painted each row's background over
    // them, so neither showed. Order is now: row fill → weekend/holiday shading → bars →
    // dotted day rules (across the bars) → today line.
    const dayCount = totalDays + 1;
    const days = Array.from({ length: dayCount }, (_, i) => new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate() + i));
    const holidays = holidaysBetween(days[0]!, days[days.length - 1]!);

    // Draw application rows (starting from y=0)
    applications.forEach((app, index) => {
      const y = index * ROW_HEIGHT;
      const isCompleted = app.schedule_status === 'Completed';

      // Row background. Owner request 2026-11-04: no highlight on the hovered row.
      if (index % 2 === 0) {
        ctx.fillStyle = WS_COLOR.surface;
      } else {
        ctx.fillStyle = WS_COLOR.surfaceSubtle;
      }
      ctx.fillRect(0, y, canvasWidth, ROW_HEIGHT);

      // Weekends and holidays shaded through every row.
      days.forEach((d, i) => {
        const kind = nonWorkingKind(d, holidays);
        if (kind) shadeDay(ctx, kind, i * DAY_WIDTH, y, DAY_WIDTH, ROW_HEIGHT);
      });

      // Row border
      ctx.strokeStyle = WS_COLOR.border;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, y + ROW_HEIGHT);
      ctx.lineTo(canvasWidth, y + ROW_HEIGHT);
      ctx.stroke();

      // Draw phase bars
      const phaseBarHeight = 24;
      const phaseY = y + (ROW_HEIGHT - phaseBarHeight) / 2;

      app.phases.forEach((phase, phaseIndex) => {
        if (!phase.start_date || !phase.end_date) return;

        const phaseStart = parseDate(phase.start_date);
        const phaseEnd = parseDate(phase.end_date);
        if (!phaseStart || !phaseEnd) return;

        const startOffset = daysBetween(minDate, phaseStart);
        const endOffset = daysBetween(minDate, phaseEnd);
        const phaseX = startOffset * DAY_WIDTH;
        const phaseWidth = Math.max((endOffset - startOffset + 1) * DAY_WIDTH, 2);

        // Use grey colors for completed or deployed applications
        const useGrayscale = isCompleted || app.deployment_status === 'Deployed';
        const fill = useGrayscale ? SOFT_GREY_FILL : (PHASE_SOFT_FILL[phase.name] || SOFT_GREY_FILL);
        const isPhaseHovered = hoveredPhase?.appId === app.id && hoveredPhase?.phase === phase.name;

        // Soft fill only — no gradient, no outline (owner, 2026-11-03: "just fill"). A hovered
        // phase deepens slightly instead of gaining a border.
        ctx.fillStyle = fill;
        ctx.beginPath();
        const radius = 4;
        ctx.roundRect(phaseX, phaseY, phaseWidth, phaseBarHeight, radius);
        ctx.fill();
        if (isPhaseHovered) {
          ctx.fillStyle = 'rgba(0, 0, 0, 0.08)';
          ctx.fill();
        }

        // Buffer indicator (lighter section at end)
        if (phase.buffer_days > 0 && phaseWidth > 20) {
          const bufferWidth = Math.min(phase.buffer_days * DAY_WIDTH, phaseWidth * 0.3);
          ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
          ctx.fillRect(phaseX + phaseWidth - bufferWidth, phaseY, bufferWidth, phaseBarHeight);
        }

        // Phase label (if wide enough)
        if (phaseWidth > 100) {
          // Dark text on the soft fills (white was for the old saturated bars).
          ctx.fillStyle = WS_COLOR.text;
          ctx.font = canvasFont(WS_TYPE.meta[0]);
          // Owner request 2026-11-04: the full phase name, not its first word; shortened with an
          // ellipsis only when the bar is too narrow for it.
          const room = phaseWidth - 12;
          let label = phase.name;
          if (ctx.measureText(label).width > room) {
            while (label.length > 1 && ctx.measureText(label + '…').width > room) label = label.slice(0, -1);
            label = label.trimEnd() + '…';
          }
          ctx.fillText(label, phaseX + 6, phaseY + 16);
        }
      });
    });

    // Dotted day rules across every row AND across the bars (owner, 2026-11-03: "break the
    // duration line by days"); off below 6px a day, where they would merge into a grey wash.
    if (DAY_WIDTH >= 6) {
      ctx.strokeStyle = GANTT_DAY_RULE;
      ctx.lineWidth = 1;
      ctx.setLineDash([1, 2]);
      for (let i = 1; i <= dayCount; i++) {
        const x = Math.round(i * DAY_WIDTH) - 0.5;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvasHeight);
        ctx.stroke();
      }
      ctx.setLineDash([]);
    }

    // TODAY line, last so nothing paints over it.
    const todayOffset = daysBetween(minDate, today);
    if (todayOffset >= 0 && todayOffset <= totalDays) {
      const todayX = todayOffset * DAY_WIDTH;

      ctx.strokeStyle = WS_STATUS.error.ink;
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(todayX, 0);
      ctx.lineTo(todayX, canvasHeight);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }, [applications, zoomLevel, hoveredApp, hoveredPhase, getTimelineBounds, DAY_WIDTH]);

  // Redraw on state changes
  useEffect(() => {
    if (viewMode === 'gantt') {
      drawGanttChart();
    }
  }, [viewMode, drawGanttChart]);

  // Sync scroll between sidebar and timeline
  const handleTimelineScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (sidebarRef.current) {
      sidebarRef.current.scrollTop = e.currentTarget.scrollTop;
    }
  }, []);

  const handleSidebarScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (timelineRef.current) {
      timelineRef.current.scrollTop = e.currentTarget.scrollTop;
    }
  }, []);

  // Canvas mouse handling
  const handleCanvasMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Determine which row (no header offset since canvas only has rows)
    const rowIndex = Math.floor(y / ROW_HEIGHT);
    if (rowIndex >= 0 && rowIndex < applications.length) {
      const app = applications[rowIndex]!;
      setHoveredApp(app.id);

      // Determine which phase
      const { minDate } = getTimelineBounds();
      const dayOffset = x / DAY_WIDTH;
      const hoverDate = new Date(minDate.getTime() + dayOffset * 24 * 60 * 60 * 1000);

      let foundPhase: string | null = null;
      app.phases.forEach(phase => {
        const phaseStart = parseDate(phase.start_date);
        const phaseEnd = parseDate(phase.end_date);
        if (phaseStart && phaseEnd && hoverDate >= phaseStart && hoverDate <= phaseEnd) {
          foundPhase = phase.name;
        }
      });

      if (foundPhase) {
        setHoveredPhase({ appId: app.id, phase: foundPhase });
      } else {
        setHoveredPhase(null);
      }
    } else {
      setHoveredApp(null);
      setHoveredPhase(null);
    }
  }, [applications, getTimelineBounds, DAY_WIDTH]);

  const handleCanvasMouseLeave = useCallback(() => {
    setHoveredApp(null);
    setHoveredPhase(null);
  }, []);

  // Export to Excel
  const exportToExcel = async () => {
    try {
      const XLSX = await import('xlsx');

      const workbook = XLSX.utils.book_new();

      // Sheet 1: Summary
      const summaryData = applications.map(app => ({
        'Integration Group ID': app.integration_group_id,
        'Integration Group Name': app.integration_group_name,
        'Application ID': app.id,
        'Application Name': app.text,
        'Start Date': app.start_date || '',
        'End Date': app.end_date || '',
        'Total Duration (Days)': app.total_duration_days,
        'Total Effort (Days)': app.total_effort_days,
        'Status': app.schedule_status,
        'Recommendation': app.application_status
      }));

      // Sheet 2: Phase Details with all columns from spec
      const phaseData: any[] = [];
      applications.forEach(app => {
        const rowData: any = {
          'Integration Group ID': app.integration_group_id,
          'Integration Group Name': app.integration_group_name,
          'Application ID': app.id,
          'Application Name': app.text,
          'Overall Start': app.start_date || '',
          'Overall End': app.end_date || ''
        };

        // Add phase columns
        app.phases.forEach((phase, index) => {
          const phaseNum = index + 1;
          const shortName = phase.name.replace(/ /g, '_');
          rowData[`P${phaseNum}_Name`] = phase.name;
          rowData[`P${phaseNum}_Base_Days`] = phase.base_duration;
          rowData[`P${phaseNum}_Buffer_Days`] = phase.buffer_days;
          rowData[`P${phaseNum}_Total_Days`] = phase.total_duration;
          rowData[`P${phaseNum}_Start`] = phase.start_date || '';
          rowData[`P${phaseNum}_End`] = phase.end_date || '';
        });

        phaseData.push(rowData);
      });

      const summarySheet = XLSX.utils.json_to_sheet(summaryData);
      const phaseSheet = XLSX.utils.json_to_sheet(phaseData);

      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Roadmap Summary');
      XLSX.utils.book_append_sheet(workbook, phaseSheet, 'Phase Details');

      const fileName = `Implementation_Roadmap_${selectedGroupId}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

    } catch (err) {
      console.error('Export error:', err);
      notify('Failed to export to Excel', 'error');
    }
  };

  // Zoom handlers
  // Steps snap to quarters so − and + land on 25% marks again after Fit to screen; 10% floor so
  // Fit can show a long programme (same rules as Roadmap Schedule).
  const handleZoomIn = () => setZoomLevel(prev => Math.min(Math.round((prev + 0.25) * 4) / 4, 3));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(Math.round((prev - 0.25) * 4) / 4, 0.1));
  const handleZoomReset = () => setZoomLevel(1);
  // Fit to screen (owner request 2026-11-03): every day fits the timeline's visible width.
  const handleFitToScreen = () => {
    const { totalDays } = getTimelineBounds();
    const el = timelineRef.current;
    if (!el || totalDays <= 0) return;
    setZoomLevel(Math.min(3, Math.max(0.1, el.clientWidth / ((totalDays + 1) * 20))));
  };

  // ============================================================================
  // Render Functions
  // ============================================================================

  // Draw timeline header on a separate canvas
  const drawTimelineHeader = useCallback((headerCanvas: HTMLCanvasElement | null) => {
    if (!headerCanvas || applications.length === 0) return;

    const ctx = headerCanvas.getContext('2d');
    if (!ctx) return;

    const { minDate, totalDays } = getTimelineBounds();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const canvasWidth = totalDays * DAY_WIDTH;

    // Set canvas size
    headerCanvas.width = canvasWidth;
    headerCanvas.height = HEADER_HEIGHT;

    // Clear and draw header background
    ctx.fillStyle = WS_COLOR.surfaceSubtle;
    ctx.fillRect(0, 0, canvasWidth, HEADER_HEIGHT);

    // DEVIATION (owner request 2026-11-03): the Roadmap Schedule Gantt's calendar header — a
    // month row (0-26px) over a day row (26-60px) carrying the day number and weekday initial,
    // weekends and holidays shaded, every day divided by a dotted rule. The source showed
    // month names only, with day numbers above 150% zoom.
    const MONTH_ROW = 26;
    const dayCount = totalDays + 1;
    const days = Array.from({ length: dayCount }, (_, i) => new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate() + i));
    const holidays = holidaysBetween(days[0]!, days[days.length - 1]!);
    const showDayLabels = DAY_WIDTH >= 16;

    // Where the TODAY tag (drawn last, below) will sit, so a month name it would cover moves
    // to just past it instead of disappearing under it.
    const todayIdx = daysBetween(minDate, today);
    ctx.font = canvasFont(WS_TYPE.meta[0], 600);
    const tagW = ctx.measureText('TODAY').width + 10;
    const tagL = todayIdx * DAY_WIDTH - tagW / 2;
    const tagR = tagL + tagW;
    const hasTag = todayIdx >= 0 && todayIdx <= totalDays;

    days.forEach((d, i) => {
      const x = i * DAY_WIDTH;
      const kind = nonWorkingKind(d, holidays);
      if (kind) shadeDay(ctx, kind, x, MONTH_ROW, DAY_WIDTH, HEADER_HEIGHT - MONTH_ROW);

      // Month label + month divider (solid, full height)
      if (i === 0 || d.getDate() === 1) {
        if (i > 0) {
          ctx.strokeStyle = WS_COLOR.border;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(Math.round(x) + 0.5, 0);
          ctx.lineTo(Math.round(x) + 0.5, HEADER_HEIGHT);
          ctx.stroke();
        }
        ctx.fillStyle = WS_COLOR.text;
        ctx.font = canvasFont(WS_TYPE.tableHeader[0], WS_TYPE.tableHeader[2]);
        const monthText = d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        let lx = x + 8;
        const lw = ctx.measureText(monthText).width;
        if (hasTag && lx < tagR + 4 && lx + lw > tagL - 4) lx = tagR + 6;
        ctx.fillText(monthText, lx, 18);
      }

      if (showDayLabels) {
        const holiday = kind === 'holiday';
        ctx.fillStyle = holiday ? GANTT_HOLIDAY_INK : WS_COLOR.textMuted;
        ctx.font = canvasFont(10, holiday ? 700 : 400);
        ctx.textAlign = 'center';
        ctx.fillText(String(d.getDate()), x + DAY_WIDTH / 2, MONTH_ROW + 14);
        ctx.fillText('SMTWTFS'[d.getDay()]!, x + DAY_WIDTH / 2, MONTH_ROW + 27);
        ctx.textAlign = 'left';
      }
    });

    // Month row bottom rule
    ctx.strokeStyle = WS_COLOR.border;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, MONTH_ROW + 0.5);
    ctx.lineTo(canvasWidth, MONTH_ROW + 0.5);
    ctx.stroke();

    // Dotted day rules through the day row
    if (DAY_WIDTH >= 6) {
      ctx.strokeStyle = GANTT_DAY_RULE;
      ctx.setLineDash([1, 2]);
      for (let i = 1; i <= dayCount; i++) {
        const x = Math.round(i * DAY_WIDTH) - 0.5;
        ctx.beginPath();
        ctx.moveTo(x, MONTH_ROW);
        ctx.lineTo(x, HEADER_HEIGHT);
        ctx.stroke();
      }
      ctx.setLineDash([]);
    }

    // Draw TODAY marker in header
    const todayOffset = daysBetween(minDate, today);
    if (todayOffset >= 0 && todayOffset <= totalDays) {
      const todayX = todayOffset * DAY_WIDTH;

      // TODAY label
      ctx.fillStyle = WS_STATUS.error.ink;
      ctx.font = canvasFont(WS_TYPE.meta[0], 600);
      const todayLabel = 'TODAY';
      const labelWidth = ctx.measureText(todayLabel).width + 10;
      ctx.fillRect(todayX - labelWidth / 2, 2, labelWidth, 16);
      ctx.fillStyle = WS_COLOR.onPrimary;
      ctx.fillText(todayLabel, todayX - labelWidth / 2 + 5, 14);
    }

    // Draw bottom border
    ctx.strokeStyle = WS_COLOR.border;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, HEADER_HEIGHT - 1);
    ctx.lineTo(canvasWidth, HEADER_HEIGHT - 1);
    ctx.stroke();
  }, [applications, zoomLevel, getTimelineBounds, DAY_WIDTH]);

  // Ref for header canvas
  const headerCanvasRef = useRef<HTMLCanvasElement>(null);

  // Draw header when needed
  useEffect(() => {
    if (viewMode === 'gantt') {
      drawTimelineHeader(headerCanvasRef.current);
    }
  }, [viewMode, drawTimelineHeader]);

  // Handle horizontal scroll sync for header
  const handleTimelineScrollWithHeader = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    // Sync sidebar vertical scroll
    if (sidebarRef.current) {
      sidebarRef.current.scrollTop = e.currentTarget.scrollTop;
    }
    // Sync header horizontal scroll
    const headerWrapper = document.querySelector('.roadmap-timeline-header-scroll') as HTMLElement;
    if (headerWrapper) {
      headerWrapper.scrollLeft = e.currentTarget.scrollLeft;
    }
  }, []);

  const renderGanttView = () => {
    const { totalDays } = getTimelineBounds();
    const canvasWidth = totalDays * DAY_WIDTH;
    const canvasHeight = applications.length * ROW_HEIGHT;

    return (
      <div className="roadmap-gantt-container">
        {/* Sidebar with application names */}
        <div className="roadmap-sidebar" style={{ width: SIDEBAR_WIDTH }}>
          {/* Fixed header for sidebar */}
          <div className="roadmap-sidebar-header" style={{ height: HEADER_HEIGHT }}>
            <span>Application</span>
          </div>
          {/* Scrollable content - synced with timeline */}
          <div
            ref={sidebarRef}
            className="roadmap-sidebar-scroll"
            onScroll={handleSidebarScroll}
          >
            <div className="roadmap-sidebar-content">
              {applications.map((app, index) => (
                <div
                  key={app.id}
                  className={`roadmap-sidebar-row ${hoveredApp === app.id ? 'hovered' : ''}`}
                  style={{ height: `${ROW_HEIGHT}px`, minHeight: `${ROW_HEIGHT}px`, maxHeight: `${ROW_HEIGHT}px` }}
                  onMouseEnter={() => setHoveredApp(app.id)}
                  onMouseLeave={() => setHoveredApp(null)}
                >
                  <div className="app-info">
                    <div className="app-id" style={{ display: 'flex', alignItems: 'center' }}>
                      {app.id}
                      <DispositionChip value={app.application_status} />
                    </div>
                    <div className="app-name" title={app.text}>{app.text}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Timeline area */}
        <div className="roadmap-timeline-wrapper">
          {/* Fixed header area for timeline - scrolls horizontally only with content */}
          <div className="roadmap-timeline-header-scroll" style={{ height: HEADER_HEIGHT, overflow: 'hidden' }}>
            <canvas
              ref={headerCanvasRef}
              width={canvasWidth}
              height={HEADER_HEIGHT}
            />
          </div>
          {/* Scrollable timeline - synced with sidebar vertically and header horizontally */}
          <div
            ref={timelineRef}
            className="roadmap-timeline"
            onScroll={handleTimelineScrollWithHeader}
          >
            <canvas
              ref={canvasRef}
              width={canvasWidth}
              height={canvasHeight}
              onMouseMove={handleCanvasMouseMove}
              onMouseLeave={handleCanvasMouseLeave}
              style={{ cursor: 'crosshair' }}
            />
          </div>
        </div>

        {/* Hover tooltip */}
        {hoveredPhase && (
          <div className="phase-tooltip">
            {(() => {
              const app = applications.find(a => a.id === hoveredPhase.appId);
              const phase = app?.phases.find(p => p.name === hoveredPhase.phase);
              if (!app || !phase) return null;

              return (
                <>
                  <div className="tooltip-header">
                    <strong>{app.id}</strong> - {hoveredPhase.phase}
                  </div>
                  <div className="tooltip-body">
                    <div><strong>Start:</strong> {formatDate(parseDate(phase.start_date))}</div>
                    <div><strong>End:</strong> {formatDate(parseDate(phase.end_date))}</div>
                    <div><strong>Base Duration:</strong> {phase.base_duration} days</div>
                    <div><strong>Buffer:</strong> {phase.buffer_days} days</div>
                    <div><strong>Total:</strong> {phase.total_duration} days</div>
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>
    );
  };

  const renderTableView = () => {
    return (
      <div className="roadmap-table-container">
        <table className="roadmap-table">
          <thead>
            <tr>
              <th rowSpan={2} className="sticky-col col-grouping">Grouping</th>
              <th rowSpan={2} className="sticky-col col-application">Application</th>
              <th rowSpan={2} className="two-line-header">
                <div>Application</div>
                <div>Disposition</div>
                <div>Start Date</div>
              </th>
              <th rowSpan={2} className="two-line-header">
                <div>Application</div>
                <div>Disposition</div>
                <div>End Date</div>
              </th>
              <th rowSpan={2} className="two-line-header">
                <div>Duration</div>
                <div>(Calendar</div>
                <div>Days)</div>
              </th>
              <th rowSpan={2} className="two-line-header">
                <div>Duration</div>
                <div>(Business</div>
                <div>Days)</div>
              </th>
              {PHASE_ORDER.map((phase) => (
                <th
                  key={phase}
                  colSpan={3}
                  className="phase-header"
                  style={{
                    backgroundColor: PHASE_COLORS[phase]?.primary || WS_COLOR.textMuted,
                    color: WS_COLOR.onPrimary
                  }}
                >
                  {phase}
                </th>
              ))}
            </tr>
            <tr>
              {PHASE_ORDER.map(phase => (
                <React.Fragment key={`${phase}-sub`}>
                  <th
                    className="sub-header"
                    style={{ backgroundColor: PHASE_COLORS[phase]?.bg || WS_COLOR.surfaceSubtle }}
                  >
                    Duration
                  </th>
                  <th
                    className="sub-header"
                    style={{ backgroundColor: PHASE_COLORS[phase]?.bg || WS_COLOR.surfaceSubtle }}
                  >
                    Start Date
                  </th>
                  <th
                    className="sub-header"
                    style={{ backgroundColor: PHASE_COLORS[phase]?.bg || WS_COLOR.surfaceSubtle }}
                  >
                    End Date
                  </th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {applications.map(app => (
              <tr key={app.id} className={(app.schedule_status === 'Completed' || app.deployment_status === 'Deployed') ? 'completed-row' : ''}>
                <td className="sticky-col col-grouping stacked-cell">
                  <div className="stacked-id">{app.integration_group_id}</div>
                  {app.integration_group_name && (
                    <div className="stacked-name" title={app.integration_group_name}>{app.integration_group_name}</div>
                  )}
                </td>
                <td className="sticky-col col-application stacked-cell">
                  <div className="stacked-id" style={{ display: 'flex', alignItems: 'center' }}>
                    {app.id}
                    <DispositionChip value={app.application_status} />
                  </div>
                  {app.text && (
                    <div className="stacked-name" title={app.text}>{app.text}</div>
                  )}
                </td>
                <td className="date-cell">{formatDateMMDDYYYY(parseDate(app.start_date))}</td>
                <td className="date-cell">{formatDateMMDDYYYY(parseDate(app.end_date))}</td>
                <td className="number-cell">{app.total_duration_days}</td>
                <td className="number-cell">
                  {/* Sum of business days from all phases */}
                  {app.phases.reduce((sum, phase) => {
                    const phaseStart = parseDate(phase.start_date);
                    const phaseEnd = parseDate(phase.end_date);
                    if (phaseStart && phaseEnd) {
                      return sum + businessDaysBetween(phaseStart, phaseEnd);
                    }
                    return sum;
                  }, 0) || '-'}
                </td>
                {PHASE_ORDER.map(phaseName => {
                  const phase = app.phases.find(p => p.name === phaseName);
                  const bgColor = PHASE_COLORS[phaseName]?.bg || WS_COLOR.surfaceSubtle;
                  const phaseStartDate = parseDate(phase?.start_date || null);
                  const phaseEndDate = parseDate(phase?.end_date || null);
                  // Calculate duration from actual dates
                  const phaseDuration = (phaseStartDate && phaseEndDate)
                    ? businessDaysBetween(phaseStartDate, phaseEndDate)
                    : '-';
                  return (
                    <React.Fragment key={`${app.id}-${phaseName}`}>
                      <td className="number-cell" style={{ backgroundColor: bgColor }}>
                        {phaseDuration}
                      </td>
                      <td className="date-cell" style={{ backgroundColor: bgColor }}>
                        {formatDateMMDDYYYY(phaseStartDate)}
                      </td>
                      <td className="date-cell" style={{ backgroundColor: bgColor }}>
                        {formatDateMMDDYYYY(phaseEndDate)}
                      </td>
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // ============================================================================
  // Main Render
  // ============================================================================

  return (
    <div className="page roadmap-page">
      <HelpPanel help={ImplementationRoadmapHelp} />
      {dialogs}
      {/* Header */}
      <PageHeader
        title="Roadmap Visualization"
        subtitle="Visualize opportunities and roadmap schedules"
        actions={applications.length > 0 ? (
          <button
            onClick={exportToExcel}
            className="btn"
            title="Download current data as CSV"
          >
            <Icon icon={icons.Download} size={20} /> Download Data
          </button>
        ) : undefined}
      />

      <div className="roadmap-content">
        {/* Controls */}
        <div className="roadmap-controls">
          {/* View Toggle */}
          <div className="control-group">
            <div className="view-toggle">
              <button
                className={`toggle-btn ${viewMode === 'gantt' ? 'active' : ''}`}
                onClick={() => setViewMode('gantt')}
              >
                Gantt Chart
              </button>
              <button
                className={`toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
                onClick={() => setViewMode('table')}
              >
                Table
              </button>
            </div>
          </div>

          {/* Project Filter */}
          <div className="control-group">
            <GroupMultiSelect
              testId="group-picker"
              label=""
              placeholder="Select Grouping"
              groups={integrationGroups.map(group => ({ id: group.group_id, description: group.group_name }))}
              value={selectedGroupIds}
              onChange={setSelectedGroupIds}
            />
          </div>

          {/* Start Date Picker (Gantt only) */}
          {viewMode === 'gantt' && (
            <div className="control-group">
              <label>Start Date:</label>
              <input
                type="date"
                value={ganttStartDate}
                onChange={(e) => setGanttStartDate(e.target.value)}
                className="group-select"
                style={{ minWidth: '150px' }}
              />
              {ganttStartDate && (
                <button
                  onClick={() => setGanttStartDate('')}
                  className="zoom-btn reset"
                  style={{ marginLeft: '4px' }}
                  title="Reset to auto-detect"
                >
                  Reset
                </button>
              )}
            </div>
          )}

          {/* Zoom Controls (Gantt only) */}
          {viewMode === 'gantt' && (
            <div className="control-group">
              <label>Zoom:</label>
              <div className="zoom-controls">
                <button onClick={handleZoomOut} className="zoom-btn" disabled={zoomLevel <= 0.1} aria-label="Zoom out">
                  <Icon icon={icons.Minus} size={20} />
                </button>
                <span className="zoom-level">{Math.round(zoomLevel * 100)}%</span>
                <button onClick={handleZoomIn} className="zoom-btn" disabled={zoomLevel >= 3} aria-label="Zoom in">
                  <Icon icon={icons.Plus} size={20} />
                </button>
                <button onClick={handleFitToScreen} className="zoom-btn reset" title="Show the whole roadmap in the visible width">
                  Fit to screen
                </button>
                <button onClick={handleZoomReset} className="zoom-btn reset">
                  Reset
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Phase Legend */}
        <div className="phase-legend">
          <span className="legend-title">Phases:</span>
          {PHASE_ORDER.map(phase => (
            <div key={phase} className="legend-item">
              <div
                className="legend-color"
                style={{ backgroundColor: PHASE_SOFT_FILL[phase] || WS_COLOR.textMuted }}
              />
              <span>{phase}</span>
            </div>
          ))}
          <div className="legend-item">
            <div className="legend-color" style={{ backgroundColor: SOFT_GREY_FILL }} />
            <span>Deployed/Completed (Grayscale)</span>
          </div>
          <div className="legend-item">
            <div className="legend-color" style={{ backgroundColor: GANTT_WEEKEND_FILL }} />
            <span>Weekend</span>
          </div>
          <div className="legend-item">
            <div className="legend-color" style={{ background: `repeating-linear-gradient(135deg, ${GANTT_HOLIDAY_BASE} 0 5px, ${GANTT_HOLIDAY_HATCH} 5px 7px)` }} />
            <span>US federal holiday (non-working)</span>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="roadmap-loading">
            <div className="spinner"></div>
            <p>Loading roadmap data...</p>
          </div>
        ) : error ? (
          <div className="roadmap-error">
            <p>{error}</p>
            <button onClick={() => setSelectedGroupIds([...selectedGroupIds])} className="btn">
              Retry
            </button>
          </div>
        ) : !selectedGroupId ? (
          <div className="roadmap-empty">
            <div className="empty-icon"><Icon icon={icons.Search} size={24} /></div>
            <div style={{ ...wsType('label'), marginBottom: '8px' }}>Select a Group</div>
            <h3>Select Integration Group</h3>
            <p>Select a specific integration group from the dropdown above to view the roadmap visualization.</p>
          </div>
        ) : applications.length === 0 ? (
          <div className="roadmap-empty">
            <div className="empty-icon"><Icon icon={icons.Inbox} size={24} /></div>
            <div style={{ ...wsType('label'), marginBottom: '8px' }}>No Data</div>
            <h3>No Schedule Data</h3>
            <p>No scheduled applications found for this group. Please ensure schedule data has been created in the Roadmap Schedule screen.</p>
          </div>
        ) : (
          <>
            {/* View Content */}
            {viewMode === 'gantt' ? renderGanttView() : renderTableView()}
          </>
        )}
      </div>
    </div>
  );
};

export default ImplementationRoadmap;
